import { useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../components/ui';
import { getInviteMedia, uploadInviteFile } from '../../utils/whatsappInvite';

// The two invite media slots an event can carry (used by the WhatsApp Image /
// PDF invite buttons on the Guests page). Media is stored per browser/device in
// localStorage (see utils/whatsappInvite.js).
export const SLOTS = [
  { kind: 'image', title: 'Image Invite', accept: 'image/*' },
  { kind: 'pdf', title: 'PDF Invite', accept: 'application/pdf' },
];

// All Invite Forms logic — shared by InviteForms.desktop / InviteForms.mobile.
export function useInviteForms() {
  const { organiser, event, can, isAdmin } = useAuth();
  const toast = useToast();
  const [version, setVersion] = useState(0);
  const [picked, setPicked] = useState({});   // kind → File
  const [busy, setBusy] = useState('');       // kind being uploaded

  const allowed = isAdmin || can('CanMembers');
  const eventTitle = useMemo(() => event?.EventName || 'Select an event', [event]);
  const hasEvent = !!event?.EventId;

  // Current media per slot; `version` forces a re-read after an upload.
  const slots = useMemo(() => SLOTS.map(slot => ({
    ...slot,
    media: hasEvent ? getInviteMedia(slot.kind, event, organiser) : null,
    picked: picked[slot.kind] || null,
    busy: busy === slot.kind,
  })), [event, organiser, hasEvent, picked, busy, version]); // eslint-disable-line react-hooks/exhaustive-deps

  const pick = (kind, file) => setPicked(p => ({ ...p, [kind]: file || null }));

  const upload = async (kind) => {
    const slot = SLOTS.find(s => s.kind === kind);
    const file = picked[kind];
    if (!file) return toast('Select a file first');
    setBusy(kind);
    try {
      await uploadInviteFile(file, kind, event, organiser);
      setPicked(p => ({ ...p, [kind]: null }));
      setVersion(v => v + 1);
      toast(`${slot.title} uploaded`);
    } catch (err) {
      toast(err.message || 'Upload failed');
    } finally {
      setBusy('');
    }
  };

  return { allowed, hasEvent, eventTitle, slots, pick, upload };
}
